/**
 * Sprint 23.9 (Fix 3 / S23.9.07) — chat error classification.
 *
 * The AI SDK chat transport throws with the raw response body as the Error
 * message on a non-2xx response (verified in `ai/dist`: it uses
 * `await response.text()`), so for a Kuvio worker error `chatError.message` is
 * the JSON envelope `{"error":"<code>","message":"<reason>"}` that
 * handleAiChat / handleAgentChat now emit (Fix 4). These helpers parse that
 * envelope and map it to a friendly title + the REAL reason + whether an inline
 * retry makes sense. Unknown / non-JSON errors (e.g. the SDK's own
 * "Failed to fetch the chat response.") fall back to the raw text, capped.
 *
 * Error classes (documented in [[design-chat-error-handling]]):
 *  - ai_limit_reached       → plan-limit card (rendered separately in AgentThread)
 *  - no_root_node           → workspace not set up for chat
 *  - not_agent              → target isn't an agent
 *  - missing_cf_aig_token   → server misconfig (dev)
 *  - chat_failed /
 *    workspace_chat_failed  → real server error (retryable)
 *  - invalid_json /
 *    messages_required      → bad request
 *  - rate_limit / timeout /
 *    network                → transient (retryable)
 */

interface WorkerError {
  /** The `error` field of the worker envelope, or "" when unparseable. */
  code: string;
  /** The human `message` of the envelope, or the raw error text. */
  message: string;
}

/**
 * Pull the `{error, message}` envelope out of an AI SDK chat error. The raw
 * message is usually pure JSON, but we slice from the first `{` to the last `}`
 * to tolerate any prefix/suffix the transport might add.
 */
function parseWorkerError(err: Error | undefined): WorkerError {
  const raw = err?.message ?? "";
  const start = raw.indexOf("{");
  const end = raw.lastIndexOf("}");
  if (start !== -1 && end > start) {
    try {
      const parsed = JSON.parse(raw.slice(start, end + 1)) as { error?: string; message?: string };
      if (parsed && (parsed.error || parsed.message)) {
        return { code: parsed.error ?? "", message: parsed.message ?? raw };
      }
    } catch {
      /* not JSON — fall through to the raw text */
    }
  }
  return { code: "", message: raw };
}

const TITLES = {
  ai_limit_reached: "AI message limit reached",
  no_root_node: "This workspace isn't set up for chat",
  not_agent: "Can't chat with this — it's not an agent",
  invalid_json: "Bad request",
  messages_required: "Nothing to send",
  missing_cf_aig_token: "Chat is misconfigured (server)",
  rate_limit: "Too many requests — slow down",
  workspace_chat_failed: "Workspace chat failed",
  chat_failed: "Something went wrong",
} as const;

/** A short, human title for the error card header. */
export function getErrorTitle(err: Error): string {
  const { code, message } = parseWorkerError(err);
  if (code && code in TITLES) return TITLES[code as keyof typeof TITLES];
  // Substring fallback — covers raw text and codes not in the table above.
  const m = `${code} ${message}`.toLowerCase();
  if (m.includes("ai_limit") || m.includes("ai message limit")) return TITLES.ai_limit_reached;
  if (m.includes("no_root_node")) return TITLES.no_root_node;
  if (m.includes("not_agent")) return TITLES.not_agent;
  if (m.includes("missing_cf_aig_token")) return TITLES.missing_cf_aig_token;
  if (m.includes("rate_limit") || m.includes("rate limit")) return TITLES.rate_limit;
  if (m.includes("timeout") || m.includes("timed out")) return "Response timed out";
  if (m.includes("network") || m.includes("failed to fetch")) return "Network problem";
  return "Something went wrong";
}

/** The body line — the worker's actual reason when we have one, else a hint. */
export function getErrorBody(err: Error): string {
  const { message } = parseWorkerError(err);
  const trimmed = (message ?? "").trim();
  // Surface the real reason, but never dump raw JSON or an essay at the user.
  if (trimmed.length > 0 && trimmed.length < 300 && !trimmed.startsWith("{")) {
    return trimmed;
  }
  return "Please try again. If this keeps happening, check the workspace settings or contact support.";
}

// Plan-limit gets its own dedicated card (not a retry); a server misconfig
// won't fix itself on retry. Everything else is worth a "Try again".
const NON_RETRYABLE = new Set(["ai_limit_reached", "missing_cf_aig_token"]);

/** Whether to offer an inline "Try again" button for this error. */
export function getErrorRetryable(err: Error): boolean {
  const { code } = parseWorkerError(err);
  if (code && NON_RETRYABLE.has(code)) return false;
  const m = (err?.message ?? "").toLowerCase();
  if (m.includes("ai_limit") || m.includes("ai message limit")) return false;
  return true;
}
